import React, { useCallback, useEffect, useRef, useState } from "react";
import { Modal, ModalBody, ModalFooter, Spinner } from "reactstrap";
import type { IconDefinition } from "@fortawesome/fontawesome-svg-core";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faBolt,
  faCamera,
  faCircleHalfStroke,
  faCrop,
  faDownload,
  faImage,
  faLocationDot,
  faMoon,
  faRotateLeft,
  faRotateRight,
  faSun,
  faUndo,
  faWandMagicSparkles,
} from "@fortawesome/free-solid-svg-icons";
import { useIntl } from "react-intl";
import { AiImageCropModal } from "./AiImageCropModal";
import { applyCanvasFilterToBlob } from "../../../cores/helpers/apply-canvas-filter-to-blob";
import { rotateImageSrcToBlobUrl } from "../../../cores/helpers/rotate-image-to-blob-url";
import "./AiImageViewerEditorModal.scss";

type FilterPreset = {
  id: string;
  labelKey: string;
  css: string;
  icon: IconDefinition;
};

const FILTER_PRESETS: FilterPreset[] = [
  { id: "none", labelKey: "app.aiGeneration.viewerFilterOriginal", css: "", icon: faImage },
  {
    id: "grayscale",
    labelKey: "app.aiGeneration.viewerFilterBw",
    css: "grayscale(1)",
    icon: faCircleHalfStroke,
  },
  { id: "sepia", labelKey: "app.aiGeneration.viewerFilterSepia", css: "sepia(0.55)", icon: faCamera },
  {
    id: "warm",
    labelKey: "app.aiGeneration.viewerFilterWarm",
    css: "sepia(0.12) saturate(1.15) brightness(1.03)",
    icon: faSun,
  },
  {
    id: "cool",
    labelKey: "app.aiGeneration.viewerFilterCool",
    css: "saturate(1.1) hue-rotate(8deg) brightness(1.02)",
    icon: faMoon,
  },
  {
    id: "vivid",
    labelKey: "app.aiGeneration.viewerFilterVivid",
    css: "contrast(1.12) saturate(1.2)",
    icon: faBolt,
  },
];

function buildFilterCss(presetCss: string, brightnessPct: number): string {
  const b = brightnessPct / 100;
  const parts = [presetCss.trim(), `brightness(${b})`].filter(Boolean);
  return parts.join(" ");
}

type ImagePin = {
  key: string;
  xPct: number;
  yPct: number;
};

function clampPct(n: number) {
  return Math.max(0, Math.min(100, Math.round(n)));
}

function buildPinnedAiPrompt(userPrompt: string, pins: ImagePin[]) {
  const instruction = userPrompt.trim();
  if (!pins.length) return instruction;
  const zones = pins
    .map(
      (pin, index) =>
        `${index + 1}. (x: ${pin.xPct}%, y: ${pin.yPct}%)`
    )
    .join("\n");
  return [
    "Zonas marcadas en la imagen (origen arriba-izquierda; porcentajes del ancho y alto):",
    zones,
    instruction
      ? `Instrucción del usuario: ${instruction}`
      : "Instrucción del usuario: mejora la zona marcada.",
    "Aplica el cambio priorizando las zonas numeradas y conserva el resto de la composición.",
  ].join("\n");
}

type Props = {
  isOpen: boolean;
  imageSrc: string | null;
  onClose: () => void;
  onApply: (blob: Blob) => void | Promise<void>;
  busy?: boolean;
  /** Optional AI edit prompt panel (e.g. Social Automated Posts). */
  enableAiEdit?: boolean;
  aiEditBusy?: boolean;
  onAiEdit?: (prompt: string) => void | Promise<void>;
  aiCreditHint?: string;
};

export function AiImageViewerEditorModal({
  isOpen,
  imageSrc,
  onClose,
  onApply,
  busy = false,
  enableAiEdit = false,
  aiEditBusy = false,
  onAiEdit,
  aiCreditHint,
}: Props) {
  const intl = useIntl();
  const [displaySrc, setDisplaySrc] = useState<string | null>(null);
  const [filterPresetId, setFilterPresetId] = useState("none");
  const [brightness, setBrightness] = useState(100);
  const [cropOpen, setCropOpen] = useState(false);
  const [cropBusy, setCropBusy] = useState(false);
  const [rotateBusyDir, setRotateBusyDir] = useState<null | "cw" | "ccw">(null);
  const [exportBusy, setExportBusy] = useState(false);
  const [applyError, setApplyError] = useState<string | null>(null);
  const [aiPrompt, setAiPrompt] = useState("");
  const [pinMode, setPinMode] = useState(false);
  const [pins, setPins] = useState<ImagePin[]>([]);
  const ownedUrlsRef = useRef<Set<string>>(new Set());
  const initialSrcRef = useRef<string | null>(null);
  const previewImgRef = useRef<HTMLImageElement | null>(null);

  const revokeOwned = useCallback((url: string | null) => {
    if (url && ownedUrlsRef.current.has(url)) {
      URL.revokeObjectURL(url);
      ownedUrlsRef.current.delete(url);
    }
  }, []);

  useEffect(() => {
    if (!isOpen) {
      ownedUrlsRef.current.forEach((u) => {
        try {
          URL.revokeObjectURL(u);
        } catch {
          /* empty */
        }
      });
      ownedUrlsRef.current.clear();
      setDisplaySrc(null);
      setCropOpen(false);
      setFilterPresetId("none");
      setBrightness(100);
      setApplyError(null);
      setAiPrompt("");
      setPinMode(false);
      setPins([]);
      initialSrcRef.current = null;
      return;
    }
    if (!imageSrc) return;
    initialSrcRef.current = imageSrc;
    setDisplaySrc(imageSrc);
    setFilterPresetId("none");
    setBrightness(100);
    setApplyError(null);
    setCropOpen(false);
    setAiPrompt("");
    setPins([]);
    setPinMode(enableAiEdit);
  }, [isOpen, imageSrc, enableAiEdit]);

  const presetCss =
    FILTER_PRESETS.find((p) => p.id === filterPresetId)?.css ?? "";
  const previewFilter = buildFilterCss(presetCss, brightness);

  const handleCropApply = async (blob: Blob) => {
    const url = URL.createObjectURL(blob);
    ownedUrlsRef.current.add(url);
    setDisplaySrc((prev) => {
      revokeOwned(prev);
      return url;
    });
    setCropOpen(false);
  };

  const handleRotate = async (direction: "cw" | "ccw") => {
    if (!displaySrc || rotateBusyDir) return;
    setRotateBusyDir(direction);
    setApplyError(null);
    try {
      const url = await rotateImageSrcToBlobUrl(displaySrc, direction);
      ownedUrlsRef.current.add(url);
      setDisplaySrc((prev) => {
        revokeOwned(prev);
        return url;
      });
    } catch {
      setApplyError(intl.formatMessage({ id: "app.aiGeneration.viewerRotateError" }));
    } finally {
      setRotateBusyDir(null);
    }
  };

  const handleResetEdits = () => {
    if (!initialSrcRef.current) return;
    setFilterPresetId("none");
    setBrightness(100);
    setPins([]);
    setDisplaySrc((prev) => {
      revokeOwned(prev);
      return initialSrcRef.current;
    });
  };

  const handleDownload = async () => {
    if (!displaySrc) return;
    setExportBusy(true);
    setApplyError(null);
    try {
      const blob = await applyCanvasFilterToBlob(displaySrc, previewFilter);
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `wembii-${Date.now()}.jpg`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch {
      setApplyError(intl.formatMessage({ id: "app.aiGeneration.viewerExportError" }));
    } finally {
      setExportBusy(false);
    }
  };

  const handleApplyToResult = async () => {
    if (!displaySrc) return;
    setExportBusy(true);
    setApplyError(null);
    try {
      const blob = await applyCanvasFilterToBlob(displaySrc, previewFilter);
      await onApply(blob);
    } catch {
      setApplyError(intl.formatMessage({ id: "app.aiGeneration.viewerExportError" }));
    } finally {
      setExportBusy(false);
    }
  };

  const handleClose = () => {
    if (busy || exportBusy || cropBusy || rotateBusyDir) return;
    setCropOpen(false);
    onClose();
  };

  const locked = busy || exportBusy || !!rotateBusyDir || aiEditBusy;

  const handlePreviewClick = (e: React.MouseEvent<HTMLDivElement>) => {
    if (!enableAiEdit || !pinMode || locked) return;
    const img = previewImgRef.current;
    if (!img) return;
    const rect = img.getBoundingClientRect();
    if (rect.width <= 0 || rect.height <= 0) return;
    const xPct = clampPct(((e.clientX - rect.left) / rect.width) * 100);
    const yPct = clampPct(((e.clientY - rect.top) / rect.height) * 100);
    setPins((prev) => [
      ...prev,
      {
        key: `pin-${Date.now()}-${prev.length}`,
        xPct,
        yPct,
      },
    ]);
  };

  const removePin = (key: string) => {
    setPins((prev) => prev.filter((pin) => pin.key !== key));
  };

  const handleAiEditSubmit = async () => {
    if (!onAiEdit || locked) return;
    if (!aiPrompt.trim() && pins.length === 0) return;
    setApplyError(null);
    try {
      await onAiEdit(buildPinnedAiPrompt(aiPrompt, pins));
      setAiPrompt("");
      setPins([]);
    } catch {
      setApplyError(
        intl.formatMessage({
          id: "app.aiGeneration.viewerAiEditError",
          defaultMessage: "No se pudo editar la imagen con IA.",
        })
      );
    }
  };

  return (
    <>
      <Modal
        isOpen={isOpen && !!displaySrc && !cropOpen}
        toggle={handleClose}
        className="ai-image-viewer-modal"
        contentClassName="ai-image-viewer-modal__dialog-content"
        modalClassName="ai-image-viewer-modal__backdrop-layer"
        backdropClassName="ai-image-viewer-modal__backdrop"
        centered
        backdrop="static"
        fade={false}
        zIndex={2050}
      >
        <div className="ai-image-viewer-modal__chrome">
          <div className="ai-image-viewer-modal__header">
            <div className="ai-image-viewer-modal__header-brand">
              <span className="ai-image-viewer-modal__badge" aria-hidden>
                <FontAwesomeIcon icon={faWandMagicSparkles} />
                AI
              </span>
              <div>
                <h2 className="ai-image-viewer-modal__title">
                  {intl.formatMessage({ id: "app.aiGeneration.viewerModalTitle" })}
                </h2>
                <p className="ai-image-viewer-modal__subtitle">
                  {intl.formatMessage({ id: "app.aiGeneration.viewerModalSubtitle" })}
                </p>
              </div>
            </div>
            <button
              type="button"
              className="ai-image-viewer-modal__close"
              onClick={handleClose}
              disabled={locked}
              aria-label={intl.formatMessage({ id: "app.aiGeneration.viewerCloseBtn" })}
            >
              ×
            </button>
          </div>

          <ModalBody className="ai-image-viewer-modal__body">
            {displaySrc && (
              <>
                <div className="ai-image-viewer-modal__preview-shell">
                  <div className="ai-image-viewer-modal__preview-grid" aria-hidden />
                  <div className="ai-image-viewer-modal__preview-glow" aria-hidden />
                  <div
                    className={
                      "ai-image-viewer-modal__preview" +
                      (enableAiEdit && pinMode
                        ? " ai-image-viewer-modal__preview--pinning"
                        : "")
                    }
                  >
                    <div
                      className="ai-image-viewer-modal__preview-stage"
                      onClick={handlePreviewClick}
                      role={enableAiEdit && pinMode ? "button" : undefined}
                      tabIndex={enableAiEdit && pinMode ? 0 : undefined}
                      aria-label={
                        enableAiEdit && pinMode
                          ? intl.formatMessage({
                              id: "app.aiGeneration.viewerPinClickHint",
                              defaultMessage:
                                "Haz clic en la imagen para marcar una zona",
                            })
                          : undefined
                      }
                    >
                      <img
                        ref={previewImgRef}
                        src={displaySrc}
                        alt=""
                        style={{ filter: previewFilter || "none" }}
                        draggable={false}
                      />
                      {enableAiEdit
                        ? pins.map((pin, index) => (
                            <button
                              key={pin.key}
                              type="button"
                              className="ai-image-viewer-modal__pin"
                              style={{
                                left: `${pin.xPct}%`,
                                top: `${pin.yPct}%`,
                              }}
                              title={intl.formatMessage(
                                {
                                  id: "app.aiGeneration.viewerPinRemoveTitle",
                                  defaultMessage: "Quitar marca {n}",
                                },
                                { n: index + 1 }
                              )}
                              onClick={(e) => {
                                e.stopPropagation();
                                removePin(pin.key);
                              }}
                              disabled={locked}
                            >
                              {index + 1}
                            </button>
                          ))
                        : null}
                    </div>
                  </div>
                </div>

                <section className="ai-image-viewer-modal__section" aria-labelledby="ai-viewer-filters-label">
                  <div className="ai-image-viewer-modal__section-head">
                    <span id="ai-viewer-filters-label" className="ai-image-viewer-modal__section-title">
                      {intl.formatMessage({ id: "app.aiGeneration.viewerFiltersLabel" })}
                    </span>
                    <span className="ai-image-viewer-modal__section-hint">
                      {intl.formatMessage({ id: "app.aiGeneration.viewerFiltersHint" })}
                    </span>
                  </div>
                  <div className="ai-image-viewer-modal__filters-scroll">
                    {FILTER_PRESETS.map((p) => (
                      <button
                        key={p.id}
                        type="button"
                        className={
                          "ai-image-viewer-modal__filter-pill" +
                          (filterPresetId === p.id
                            ? " ai-image-viewer-modal__filter-pill--active"
                            : "")
                        }
                        onClick={() => setFilterPresetId(p.id)}
                        disabled={locked}
                      >
                        <FontAwesomeIcon icon={p.icon} className="ai-image-viewer-modal__filter-pill-icon" />
                        <span>{intl.formatMessage({ id: p.labelKey })}</span>
                      </button>
                    ))}
                  </div>
                </section>

                <section className="ai-image-viewer-modal__tuning" aria-labelledby="ai-viewer-brightness-label">
                  <div className="ai-image-viewer-modal__tuning-inner">
                    <div className="ai-image-viewer-modal__brightness-head">
                      <FontAwesomeIcon icon={faSun} className="ai-image-viewer-modal__brightness-icon" />
                      <span id="ai-viewer-brightness-label">
                        {intl.formatMessage({ id: "app.aiGeneration.viewerBrightnessLabel" })}
                      </span>
                      <span className="ai-image-viewer-modal__brightness-value">{brightness}%</span>
                    </div>
                    <input
                      type="range"
                      className="ai-image-viewer-modal__range"
                      min={60}
                      max={140}
                      value={brightness}
                      onChange={(e) => setBrightness(Number(e.target.value))}
                      disabled={locked}
                      aria-label={intl.formatMessage({
                        id: "app.aiGeneration.viewerBrightnessLabel",
                      })}
                    />
                  </div>

                  <div className="ai-image-viewer-modal__quick-tools">
                    <button
                      type="button"
                      className="ai-image-viewer-modal__tool-btn"
                      onClick={() => handleRotate("ccw")}
                      disabled={locked}
                      title={intl.formatMessage({
                        id: "app.aiGeneration.viewerRotateLeftTitle",
                      })}
                      aria-label={intl.formatMessage({
                        id: "app.aiGeneration.viewerRotateLeftTitle",
                      })}
                    >
                      {rotateBusyDir === "ccw" ? (
                        <Spinner size="sm" className="ai-image-viewer-modal__tool-spinner" />
                      ) : (
                        <FontAwesomeIcon icon={faRotateLeft} />
                      )}
                      <span>{intl.formatMessage({ id: "app.aiGeneration.viewerRotateLeftBtn" })}</span>
                    </button>
                    <button
                      type="button"
                      className="ai-image-viewer-modal__tool-btn"
                      onClick={() => handleRotate("cw")}
                      disabled={locked}
                      title={intl.formatMessage({
                        id: "app.aiGeneration.viewerRotateRightTitle",
                      })}
                      aria-label={intl.formatMessage({
                        id: "app.aiGeneration.viewerRotateRightTitle",
                      })}
                    >
                      {rotateBusyDir === "cw" ? (
                        <Spinner size="sm" className="ai-image-viewer-modal__tool-spinner" />
                      ) : (
                        <FontAwesomeIcon icon={faRotateRight} />
                      )}
                      <span>{intl.formatMessage({ id: "app.aiGeneration.viewerRotateRightBtn" })}</span>
                    </button>
                    <button
                      type="button"
                      className="ai-image-viewer-modal__tool-btn"
                      onClick={() => setCropOpen(true)}
                      disabled={locked}
                    >
                      <FontAwesomeIcon icon={faCrop} />
                      <span>{intl.formatMessage({ id: "app.aiGeneration.cropBtn" })}</span>
                    </button>
                    <button
                      type="button"
                      className="ai-image-viewer-modal__tool-btn"
                      onClick={handleResetEdits}
                      disabled={locked}
                    >
                      <FontAwesomeIcon icon={faUndo} />
                      <span>{intl.formatMessage({ id: "app.aiGeneration.viewerResetBtn" })}</span>
                    </button>
                    <button
                      type="button"
                      className="ai-image-viewer-modal__tool-btn"
                      onClick={handleDownload}
                      disabled={locked || exportBusy}
                    >
                      {exportBusy ? (
                        <Spinner size="sm" className="ai-image-viewer-modal__tool-spinner" />
                      ) : (
                        <FontAwesomeIcon icon={faDownload} />
                      )}
                      <span>{intl.formatMessage({ id: "app.aiGeneration.viewerDownloadBtn" })}</span>
                    </button>
                  </div>
                </section>

                {enableAiEdit && onAiEdit ? (
                  <section
                    className="ai-image-viewer-modal__section ai-image-viewer-modal__ai-edit"
                    aria-labelledby="ai-viewer-ai-edit-label"
                  >
                    <div className="ai-image-viewer-modal__section-head">
                      <span
                        id="ai-viewer-ai-edit-label"
                        className="ai-image-viewer-modal__section-title"
                      >
                        {intl.formatMessage({
                          id: "app.aiGeneration.viewerAiEditTitle",
                          defaultMessage: "Editar con IA",
                        })}
                      </span>
                    </div>
                    <div className="ai-image-viewer-modal__pin-callout" role="note">
                      <span className="ai-image-viewer-modal__pin-callout-icon" aria-hidden>
                        <FontAwesomeIcon icon={faLocationDot} />
                      </span>
                      <div className="ai-image-viewer-modal__pin-callout-body">
                        <strong className="ai-image-viewer-modal__pin-callout-title">
                          {intl.formatMessage({
                            id: "app.aiGeneration.viewerPinCalloutTitle",
                            defaultMessage: "Marca zonas en la imagen",
                          })}
                        </strong>
                        <p className="ai-image-viewer-modal__pin-callout-text">
                          {intl.formatMessage({
                            id: "app.aiGeneration.viewerAiEditHintPinned",
                            defaultMessage:
                              "Haz clic para colocar numeritos y describe el cambio. La IA recibe las coordenadas exactas.",
                          })}
                        </p>
                      </div>
                    </div>
                    <div className="ai-image-viewer-modal__pin-toolbar">
                      <button
                        type="button"
                        className={
                          "ai-image-viewer-modal__pin-cta" +
                          (pinMode
                            ? " ai-image-viewer-modal__pin-cta--active"
                            : "")
                        }
                        onClick={() => setPinMode((v) => !v)}
                        disabled={locked}
                      >
                        <FontAwesomeIcon icon={faLocationDot} />
                        <span>
                          {pinMode
                            ? intl.formatMessage({
                                id: "app.aiGeneration.viewerPinModeOn",
                                defaultMessage: "Marcando zonas…",
                              })
                            : intl.formatMessage({
                                id: "app.aiGeneration.viewerPinModeOff",
                                defaultMessage: "Marcar zona",
                              })}
                        </span>
                      </button>
                      {pins.length > 0 ? (
                        <button
                          type="button"
                          className="ai-image-viewer-modal__tool-btn"
                          onClick={() => setPins([])}
                          disabled={locked}
                        >
                          <span>
                            {intl.formatMessage(
                              {
                                id: "app.aiGeneration.viewerPinClear",
                                defaultMessage: "Limpiar marcas ({n})",
                              },
                              { n: pins.length }
                            )}
                          </span>
                        </button>
                      ) : null}
                    </div>
                    {pins.length > 0 ? (
                      <ul className="ai-image-viewer-modal__pin-list">
                        {pins.map((pin, index) => (
                          <li key={pin.key}>
                            <strong>{index + 1}.</strong>{" "}
                            (x: {pin.xPct}%, y: {pin.yPct}%)
                          </li>
                        ))}
                      </ul>
                    ) : null}
                    <textarea
                      className="ai-image-viewer-modal__ai-textarea"
                      value={aiPrompt}
                      onChange={(e) => setAiPrompt(e.target.value)}
                      disabled={locked}
                      rows={3}
                      placeholder={intl.formatMessage({
                        id: "app.aiGeneration.viewerAiEditPlaceholderPinned",
                        defaultMessage:
                          "Ej: cambia ese icono, agranda el texto, mejora el contraste…",
                      })}
                    />
                    <div className="ai-image-viewer-modal__ai-actions">
                      <button
                        type="button"
                        className="ai-image-viewer-modal__btn ai-image-viewer-modal__btn--primary"
                        onClick={handleAiEditSubmit}
                        disabled={
                          locked || (!aiPrompt.trim() && pins.length === 0)
                        }
                      >
                        {aiEditBusy ? (
                          <>
                            <Spinner size="sm" className="mr-2" />
                            {intl.formatMessage({
                              id: "app.aiGeneration.viewerAiEditApplying",
                              defaultMessage: "Aplicando con IA…",
                            })}
                          </>
                        ) : (
                          <>
                            <FontAwesomeIcon icon={faWandMagicSparkles} className="mr-2" />
                            {intl.formatMessage({
                              id: "app.aiGeneration.viewerAiEditBtn",
                              defaultMessage: "Aplicar con IA",
                            })}
                          </>
                        )}
                      </button>
                      {aiCreditHint ? (
                        <span className="ai-image-viewer-modal__ai-credit">{aiCreditHint}</span>
                      ) : null}
                    </div>
                  </section>
                ) : null}

                <p className="ai-image-viewer-modal__foot-note">
                  {intl.formatMessage({ id: "app.aiGeneration.viewerHint" })}
                </p>

                {applyError && (
                  <p className="ai-image-viewer-modal__error">{applyError}</p>
                )}
              </>
            )}
          </ModalBody>

          <ModalFooter className="ai-image-viewer-modal__footer">
            <button
              type="button"
              className="ai-image-viewer-modal__btn ai-image-viewer-modal__btn--ghost"
              onClick={handleClose}
              disabled={locked}
            >
              {intl.formatMessage({ id: "app.aiGeneration.viewerCloseBtn" })}
            </button>
            <button
              type="button"
              className="ai-image-viewer-modal__btn ai-image-viewer-modal__btn--primary"
              onClick={handleApplyToResult}
              disabled={locked || !displaySrc}
            >
              {exportBusy || busy ? (
                <>
                  <Spinner size="sm" className="ai-image-viewer-modal__btn-spinner" />
                  {intl.formatMessage({ id: "app.aiGeneration.viewerApplying" })}
                </>
              ) : (
                <>
                  <FontAwesomeIcon icon={faWandMagicSparkles} />
                  {intl.formatMessage({ id: "app.aiGeneration.viewerApplyBtn" })}
                </>
              )}
            </button>
          </ModalFooter>
        </div>
      </Modal>

      <AiImageCropModal
        isOpen={cropOpen && !!displaySrc}
        imageSrc={displaySrc}
        onClose={() => !cropBusy && setCropOpen(false)}
        onApply={async (blob) => {
          setCropBusy(true);
          try {
            await handleCropApply(blob);
          } finally {
            setCropBusy(false);
          }
        }}
        busy={cropBusy}
      />
    </>
  );
}
